import { types, flow, getEnv, clone, SnapshotOut, Instance } from 'mobx-state-tree';
import { observable, IObservableArray } from 'mobx';
import { errorIsInApiRequest, errorIsCancel, IGroupedApiCtx, LiveEvents, ProgressStateModelForStore, CancelError, ProgressStateModel, SportsBettingApiResult, IDesktopLiveEventListStore, IEventResultStore, createRenderTelemetryService } from 'eTip--common';
import { LiveCategoryStore, ILiveCategoryStore, IStreamData } from './liveCategory';
import { ISportsBettingLiveApi } from '../../api/live/api';
import { ILiveAppEnv } from '../../liveAppEnvironment';
import { LiveEventStore } from './liveEvent';
import { translation } from 'easit--localization';
import { SortType, createComparerOfSorting } from 'easit--utils';
import { IMatchTrackerSelectionItem } from 'desktop--design-library';
import { MatchTrackerRightSideWrapperStore } from './liveStreamRightSideWrapper';

export const FilterItemStore = types.model('FilterItem', {
    id: types.number,
    graphicsPresentationId: types.number,
    label: types.string,
    sequence: types.number,
    isSelected: types.optional(types.boolean, false)
});

export let SportFilterAll = 0;
export let SportFilterAllAsString = "0";

export let SportFilterVideos = -2; //same as in mobile
export let SportFilterVideosAsString = "-2";
export let SportFilterMatchTrackers = -3;
export let SportFilterMatchTrackersAsString = "-3";


/**
 * Store for live events list.
 * For MTS Sequence in Event is compound of Region/Tournament (path is shown in list for the first one).
 */
const LiveEventListKey = 'liveEventList';
export const LiveEventListStore = types
    .model(LiveEventListKey, {
        categories: types.optional(types.array(LiveCategoryStore), []),
        favoriteCats: types.optional(types.array(LiveCategoryStore), []),
        sportFilter: types.optional(types.map(FilterItemStore), {}), //for All on the first position
        detail: types.maybeNull(LiveEventStore),
        isDetailShown: types.optional(types.boolean, false),
        isDetailShownAsDefault: types.optional(types.boolean, true), //in list, use did no action to open a detail, use with isDetailShown
        lastReloadTime: types.maybeNull(types.number),
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        categoryIdForOpenedEventsPopup: types.optional(types.number, 0),
        eventsPopupOffset: types.maybeNull(types.number),
        rightSideStreamWrapper: types.optional(MatchTrackerRightSideWrapperStore, {})
    })
    .views(self => {

        const env = getEnv<ILiveAppEnv>(self);
        const entryPageRenderTelemetry = createRenderTelemetryService("Entrance page", "Desktop Live event list", env.loggingService);
        const leftColumnRenderTelemetry = createRenderTelemetryService("Left column", "Desktop Live left column", env.loggingService);

        return {
            get refreshTime() {
                return env.settings.liveEventListRefreshTimeMs;
            },
            get categoriesEventCount(): number {
                let sum = 0;
                self.categories.forEach(cat => sum += cat.eventCount);
                return sum;
            },
            get favoritesEventCount(): number {
                let sum = 0;
                self.favoriteCats.forEach(cat => sum += cat.eventCount);
                return sum;
            },
            get eventsWithMatchTracker(): Map<number, IMatchTrackerSelectionItem> {
                let result = new Map<number, IMatchTrackerSelectionItem>();
                self.favoriteCats.map(c => c.events.forEach(evt => { if (evt.matchTracker) { result.set(evt.id, { id: evt.id, matchId: evt.matchTracker.id, type: evt.matchTracker.type, title: evt.name, graphicsPresentationId: c.graphicsPresentationId }) } }));
                self.categories.map(c => c.events.forEach(evt => { if (evt.matchTracker) { result.set(evt.id, { id: evt.id, matchId: evt.matchTracker.id, type: evt.matchTracker.type, title: evt.name, graphicsPresentationId: c.graphicsPresentationId }) } })); //? order?
                return result;
            },
            get entryPageRenderTelemetry() {
                return entryPageRenderTelemetry;
            },
            get leftColumnRenderTelemetry() {
                return leftColumnRenderTelemetry;
            },
            get canFilterVideoAndMTWithSport() {
                return env.settings.canFilterVideoAndMTWithSport;
            }
        }
    })
    .actions(self => {
        const env = getEnv<ILiveAppEnv>(self);
        const LOCAL_STORAGE_KEY = 'live-discipline-filter';
        const LOCAL_STORAGE_KEY_COLLAPSED_CATEGORIES = 'live-collapsed-discipline';
        const sequenceComparer = createComparerOfSorting((c: ILiveCategoryStore) => c.sequence, SortType.Asc);

        let synchroCtx: IGroupedApiCtx<ISportsBettingLiveApi> | null = null;
        let loadedSelectedCategories: Set<number> | null = null; //expecting empty is null
        let catIdsCollapsedByUser = new Set<number>();

        /**
        * Expected to be run once.
        * FavoriteIdsProvider has to by function since ids may change during ajax.
         */
        const initAsync: (favoriteIdsProvider: () => Set<number>) => Promise<void> = flow(function* (favoriteIdsProvider: () => Set<number>) {

            if (self.sportFilter.has(SportFilterAllAsString)) {
                throw new Error("Excepted to be ran once per app life");
            }

            self.lastReloadTime = 0;
            self.state = ProgressStateModel.Loading;

            loadedSelectedCategories = loadSportSelection();

            self.sportFilter.set(SportFilterAllAsString,
                FilterItemStore.create(<SnapshotOut<typeof FilterItemStore>>{
                    id: 0,
                    graphicsPresentationId: 0,
                    label: translation().all,
                    sequence: -2,
                    isSelected: loadedSelectedCategories == null
                })
            );

            loadCollapsedCategories();

            yield loadDataCommonAsync(true, favoriteIdsProvider);
        });

        /**
         * Refresh data.
         *
         * Possible CancelError in reject.
         */
        const reloadDataAsync: (favoriteIdsProvider: () => Set<number>) => Promise<void> = flow(function* (favoriteIdsProvider: () => Set<number>) {
            yield loadDataCommonAsync(false, favoriteIdsProvider);
        });

        const selectSportAsync: (sportCategoryId: number, favoriteIdsProvider: () => Set<number>) => Promise<void> = flow(function* (_sportCategoryId: number, favoriteIdsProvider: () => Set<number>) {
            /*if (!self.selectedSportIds.remove(sportCategoryId)) {
                self.selectedSportIds.push(sportCategoryId);
            }*/
            self.lastReloadTime = null;
            yield loadDataCommonAsync(true, favoriteIdsProvider);
        });

        /**
         * Loads data (both for full or refresh, depends on self.state).
         *
         * Reject may return CancelError.
         */

        const loadDataCommonAsync: (asFullLoad: boolean, favoriteIdsProvider: () => Set<number>) => Promise<void> = flow(function* (asFullLoad: boolean, favoriteIdsProvider: () => Set<number>) {
            let ctx: IGroupedApiCtx<ISportsBettingLiveApi> | null = null;
            try {
                //env.loggingService.info("LiveEventList start " + self.loadDataId + " " + self.state);
                if (synchroCtx && synchroCtx.isOpen()) {
                    if (!asFullLoad) {
                        throw new CancelError(); //does nothing, already waiting for response
                    }
                    //env.loggingService.info("LiveEventList cancelled previous in " + self.loadDataId);
                    synchroCtx.cancel('Load has been canceled.');
                    synchroCtx.close();
                    synchroCtx = null;
                }
                if (asFullLoad) {
                    self.state = ProgressStateModel.Loading;
                }

                ctx = env.api.open();
                synchroCtx = ctx;
                const response: LiveEvents.RootObject = yield ctx.api.getLiveEventsWLAsync(
                    asFullLoad ? 0 : self.lastReloadTime
                );
                if (response.Result == SportsBettingApiResult.Success) { //also NoChange may come
                    self.lastReloadTime = response.TimeStamp;
                    setData(response.ReturnValue, favoriteIdsProvider);
                }

                self.state = ProgressStateModel.Success;
                //env.loggingService.info("LiveEventList success " + self.loadDataId);
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                if (asFullLoad || !errorIsInApiRequest(error)) {
                    self.state = ProgressStateModel.Failed;
                }
                throw error;
            } finally {
                if (ctx === synchroCtx) {
                    synchroCtx?.close();
                    synchroCtx = null;
                }
            }
        });


        const setData = (items: LiveEvents.ReturnValueItem[], favoriteIdsProvider: () => Set<number>, _asFullLoad?: boolean): void => {

            if (!items) {
                self.favoriteCats = observable([]); //TODOHL or clear?
                self.categories = observable([]);
                return;
            }

            let newCats: Instance<typeof LiveCategoryStore>[] = [];
            let touchedFavCats = new Set<number>();
            let touchedCats = new Set<number>();
            let streamData: IStreamData = { isMatchTracker: false, isVideo: false };
            items.forEach(sourceCat => {
                let favDestCat = self.favoriteCats.find(c => c.id == sourceCat.ID);
                if (favDestCat) {
                    favDestCat.updateCategoryFromApi(sourceCat);
                    touchedFavCats.add(favDestCat.id);
                }

                let destCat = self.categories.find(c => c.id == sourceCat.ID);
                if (destCat) {
                    destCat.updateCategoryFromApi(sourceCat);
                    touchedCats.add(destCat.id);
                }

                const favoriteIds = favoriteIdsProvider();
                const sourceEventsForFavorites = sourceCat.Events.filter(evt => favoriteIds.has(evt.ID));
                const sourceEventsForNormal = sourceCat.Events.filter(evt => !favoriteIds.has(evt.ID));

                if (!favDestCat && sourceEventsForFavorites.length > 0) {
                    favDestCat = LiveCategoryStore.create(<ILiveCategoryStore>{
                        isFavorite: true,
                        id: sourceCat.ID,
                        sequence: sourceCat.Sequence,
                        name: sourceCat.Name,
                        eventCount: sourceCat.EventCount,
                        graphicsPresentationId: sourceCat.GraphicsPresentationID ? sourceCat.GraphicsPresentationID : 0,
                        //isExpandedInLeftMenu default is true
                        events: []
                    }, env);
                    touchedFavCats.add(favDestCat.id);
                    self.favoriteCats.push(favDestCat);
                    //newCats.push(favDestCat);
                }

                if (!destCat && (!favDestCat || sourceEventsForNormal.length > 0)) {
                    destCat = LiveCategoryStore.create(<ILiveCategoryStore>{
                        isFavorite: false,
                        id: sourceCat.ID,
                        sequence: sourceCat.Sequence,
                        name: sourceCat.Name,
                        eventCount: sourceCat.EventCount,
                        graphicsPresentationId: sourceCat.GraphicsPresentationID ? sourceCat.GraphicsPresentationID : 0,
                        // isExpandedInLeftMenu set in updateIsExpInCategory below
                        events: [],
                        categoryPathName: null,
                        isExpandedInLeftMenu: undefined,
                        shownGameGroupId: null,
                        shownGameGroups: undefined
                    }, env);
                    touchedCats.add(destCat.id);
                    self.categories.push(destCat);
                    newCats.push(destCat);
                }

                let touchedInFav: Set<number> = null;
                let touchedInNormal: Set<number> = null;
                if (favDestCat) {
                    touchedInFav = favDestCat.updateEventsFromApi(sourceEventsForFavorites, favDestCat.graphicsPresentationId, streamData);
                }
                if (destCat) {
                    touchedInNormal = destCat.updateEventsFromApi(sourceEventsForNormal, destCat.graphicsPresentationId, streamData);
                }
                if (favDestCat) {
                    let toRemove = favDestCat.events.filter(evt => !touchedInFav.has(evt.id));
                    toRemove.forEach(evt => favDestCat.events.remove(evt));
                }

                if (destCat) {
                    let toRemove = destCat.events.filter(evt => !touchedInNormal.has(evt.id));
                    toRemove.forEach(evt => destCat.events.remove(evt));
                }
            });

            let toRemoveCats = self.favoriteCats.filter(cat => cat.events.length == 0 || !touchedFavCats.has(cat.id));
            toRemoveCats.forEach(cat => self.favoriteCats.remove(cat));

            toRemoveCats = self.categories.filter(cat => cat.events.length == 0 || !touchedCats.has(cat.id));
            toRemoveCats.forEach(cat => self.categories.remove(cat));

            self.favoriteCats.replace(self.favoriteCats.sort(sequenceComparer));
            self.categories.replace(self.categories.sort(sequenceComparer));

            updateSportFilter(streamData);
            newCats.forEach(updateIsExpandedInCategory);

            let knownFavoriteEventsWithScore = new Map<number, IEventResultStore | null>();
            self.favoriteCats.forEach(cat => cat.events.forEach(e => knownFavoriteEventsWithScore.set(e.id, e.mainResult)));
            env.scoreChangeMonitor.favoriteEventsUpdated(knownFavoriteEventsWithScore);
        };

        const updateSportFilter = (streamData: IStreamData): void => {
            let touched = new Set<number>();
            updateSportFilterItem(self.favoriteCats, touched)
            updateSportFilterItem(self.categories, touched);
            updateStreamSportFilterItem(streamData, touched);

            let toRemove: number[] = [];
            self.sportFilter.forEach(item => { if (!touched.has(item.id)) { toRemove.push(item.id); } });
            toRemove.forEach(id => {
                if (id != SportFilterAll) {
                    self.sportFilter.delete(id.toString());
                }
            });

            checkSportSelection();
        }

        const updateSportFilterItem = (sourceCats: ILiveCategoryStore[], touched: Set<number>): void => {
            sourceCats.forEach(c => {
                if (!self.sportFilter.has(c.id.toString())) {
                    let newFilter = FilterItemStore.create(<SnapshotOut<typeof FilterItemStore>>{
                        id: c.id,
                        graphicsPresentationId: c.graphicsPresentationId,
                        label: c.name,
                        sequence: c.sequence,
                        isSelected: (!!loadedSelectedCategories && loadedSelectedCategories.has(c.id))
                    });
                    self.sportFilter.set(c.id.toString(), newFilter);
                }
                touched.add(c.id);
            });
        }

        const updateStreamSportFilterItem = (streamData: IStreamData, touched: Set<number>) => {
            //order in sequence similar to mobile
            if (streamData.isVideo) {
                if (!self.sportFilter.has(SportFilterVideosAsString)) {
                    let newFilter = FilterItemStore.create(<SnapshotOut<typeof FilterItemStore>>{
                        id: SportFilterVideos,
                        graphicsPresentationId: -1,
                        label: translation().live_TabVideo,
                        sequence: -1,
                        isSelected: false //(!!loadedSelectedCategories && loadedSelectedCategories.has(c.id))
                    });
                    self.sportFilter.set(SportFilterVideosAsString, newFilter);
                }
                touched.add(SportFilterVideos);
            }
            if (streamData.isMatchTracker) {
                if (!self.sportFilter.has(SportFilterMatchTrackersAsString)) {
                    let newFilter = FilterItemStore.create(<SnapshotOut<typeof FilterItemStore>>{
                        id: SportFilterMatchTrackers,
                        graphicsPresentationId: -1,
                        label: translation().live_TabMatchTracker,
                        sequence: 2000000002,
                        isSelected: false //(!!loadedSelectedCategories && loadedSelectedCategories.has(c.id))
                    });
                    self.sportFilter.set(SportFilterMatchTrackersAsString, newFilter);
                }
                touched.add(SportFilterMatchTrackers);
            }
        }

        const showDetail = (eventId: number, isFromUserAction: boolean): void => {
            if (isFromUserAction) {
                self.isDetailShownAsDefault = false;
            }
            if (!self.detail) {
                self.detail = LiveEventStore.create(<SnapshotOut<typeof LiveEventStore>>{
                    id: eventId,
                    state: ProgressStateModel.NotInitialized
                }, env);
            } else {
                self.detail.startForNewEvent(eventId);
            }
            self.isDetailShown = true;
        };

        const hideDetail = () => {
            delete self.detail;
            self.isDetailShown = false;
            self.isDetailShownAsDefault = true;
        };
        const toggleDetail = (eventId: number): void => {
            if (!eventId || (self.detail && self.detail.id == eventId)) {
                hideDetail();
            }
            else {
                showDetail(eventId, true);
            }
        };

        const checkAndOpenDetail = () => {

            if (!self.isDetailShown) {
                let firstSelected: number = 0;
                self.sportFilter.forEach(f => {
                    if (firstSelected === 0 && f.isSelected) {
                        firstSelected = f.id;
                    }
                });

                if (firstSelected) {

                    if (firstSelected === SportFilterMatchTrackers) {
                        let cat = self.favoriteCats.find(cat => cat.hasMatchTrackerEvent);
                        if (!cat) {
                            cat = self.categories.find(cat => cat.hasMatchTrackerEvent);
                        }
                        if (cat) {
                            let evt = cat.events.find(evt => !!evt.matchTracker);
                            if (evt) {
                                showDetail(evt.id, false);
                            }
                        }
                        //incorrect state
                        return;
                    }

                    if (firstSelected === SportFilterVideos) {
                        let cat = self.favoriteCats.find(cat => cat.hasVideoEvent);
                        if (!cat) {
                            cat = self.categories.find(cat => cat.hasVideoEvent);
                        }
                        if (cat) {
                            let evt = cat.events.find(evt => !!evt.liveStream);
                            if (evt) {
                                showDetail(evt.id, false);
                            }
                        }
                        //incorrect state
                        return;
                    }

                    let selectedCategory = self.favoriteCats.find(cat => cat.id === firstSelected);
                    if (!selectedCategory) {
                        selectedCategory = self.categories.find(cat => cat.id === firstSelected);
                    }
                    showDetail(selectedCategory.events[0].id, false);
                    return;
                }

                if (self.favoriteCats.length > 0) {
                    showDetail(self.favoriteCats[0].events[0].id, false);
                    return;
                }
                if (self.categories.length > 0) {
                    showDetail(self.categories[0].events[0].id, false);
                }
            }
        }

        const createCategoryCopy = (source: ILiveCategoryStore, isFavorite: boolean): ILiveCategoryStore => {
            return LiveCategoryStore.create(<SnapshotOut<typeof LiveCategoryStore>>{
                forceCanConvertTipName: source.forceCanConvertTipName,
                isFavorite: isFavorite,
                id: source.id,
                sequence: source.sequence,
                graphicsPresentationId: source.graphicsPresentationId,
                name: source.name,
                categoryPathName: source.categoryPathName,
                isExpandedInLeftMenu: isFavorite ? true : source.isExpandedInLeftMenu,
                events: []
            }, env);
        }

        const changeCategoryOfEventForFavorites = (evt: IDesktopLiveEventListStore, sourceList: IObservableArray<ILiveCategoryStore>, destList: IObservableArray<ILiveCategoryStore>, toBeFavorite: boolean): IDesktopLiveEventListStore => {
            let sourceCat = sourceList.find(cat => !!cat.events.find(ce => ce === evt));
            let destCat = destList.find(c => c.id === sourceCat.id);
            if (!destCat) {
                destCat = createCategoryCopy(sourceCat, toBeFavorite);
                destList.push(destCat);
                destList.replace(destList.sort(sequenceComparer));
            }
            let newEvent = clone(evt, true);
            sourceCat.events.remove(evt);
            destCat.addEvent(newEvent);

            if (sourceCat.events.length == 0) {
                sourceList.remove(sourceCat);
            }
            return newEvent;
        }

        const setEventFavorite = (evt: IDesktopLiveEventListStore, value: boolean) => {
            if (value) {
                changeCategoryOfEventForFavorites(evt, self.categories, self.favoriteCats, value);
            } else {
                changeCategoryOfEventForFavorites(evt, self.favoriteCats, self.categories, value);
            }
        }

        const addFavoriteEvent = (id: number): void => {
            let evt: IDesktopLiveEventListStore = null;
            self.categories.find(cat => { evt = cat.events.find(ce => ce.id === id); return !!evt; });
            if (evt) {
                setEventFavorite(evt, true);
            }
        }

        const removeFavoriteEvent = (id: number): void => {
            let evt: IDesktopLiveEventListStore = null;
            self.favoriteCats.find(cat => { evt = cat.events.find(ce => ce.id === id); return !!evt; });
            if (evt) {
                setEventFavorite(evt, false);
            }
        }

        const removeAllFavoriteEvents = () => {
            self.favoriteCats.forEach(cat => { cat.isFavorite = false; cat.events.forEach(evt => { setEventFavorite(evt, false); }) })
        }

        const toggleSportFilterBySportCategoryId = (sportCategoryId: number) => {
            let filterItem: Instance<typeof FilterItemStore> = null;
            self.sportFilter.forEach(item => {
                if (item.id === sportCategoryId) {
                    filterItem = item;
                }
            });
            setSportFilterIsSelected(filterItem, !filterItem.isSelected);
        }

        const setSportFilterIsSelected = (item: Instance<typeof FilterItemStore>, value: boolean): void => {

            if (self.canFilterVideoAndMTWithSport) {

                // IF SPORT_FILTER_ALL SELECT
                if (item.id === SportFilterAll) {
                    if (value) {
                        self.sportFilter.forEach(fi => fi.isSelected = false);
                        item.isSelected = true;
                    }
                    storeSportSelection();
                    self.categories.forEach(updateIsExpandedInCategory);
                    return;
                } else {
                    self.sportFilter.get(SportFilterAllAsString).isSelected = false;
                }

                const existVideoFilter = self.sportFilter.has(SportFilterVideosAsString);
                const existsMatchTrackerFilter = self.sportFilter.has(SportFilterMatchTrackersAsString);

                // IF VIDEO/MATCHTRACKER SELECT
                if (existVideoFilter || existsMatchTrackerFilter) {

                    const videoFilter = existVideoFilter && self.sportFilter.get(SportFilterVideosAsString);
                    const matchTrackerFilter = existsMatchTrackerFilter && self.sportFilter.get(SportFilterMatchTrackersAsString);

                    if (item.id === SportFilterVideos) {
                        videoFilter.isSelected = !videoFilter.isSelected;
                    }
                    else if (item.id === SportFilterMatchTrackers) {
                        matchTrackerFilter.isSelected = !matchTrackerFilter.isSelected;
                    } else {
                        selectSportFilterForCanFilterVideoAndMTWithSport(item, value);
                        return;
                    }

                    storeSportSelection();
                    checkSportSelection();

                    return;
                }

                // IF SOME SPORT SELECT
                selectSportFilterForCanFilterVideoAndMTWithSport(item, value);
                return;
            }

            // !self.canFilterVideoAndMTWithSport
            if (item.id === SportFilterAll || item.id === SportFilterMatchTrackers || item.id === SportFilterVideos) {
                if (value) {
                    self.sportFilter.forEach(fi => fi.isSelected = false);
                    item.isSelected = true;
                }
                storeSportSelection();
                self.categories.forEach(updateIsExpandedInCategory);
                return;
            }
            item.isSelected = value;
            if (value) {
                self.sportFilter.get(SportFilterAllAsString).isSelected = false;
                if (self.sportFilter.has(SportFilterMatchTrackersAsString)) {
                    self.sportFilter.get(SportFilterMatchTrackersAsString).isSelected = false;
                }
                if (self.sportFilter.has(SportFilterVideosAsString)) {
                    self.sportFilter.get(SportFilterVideosAsString).isSelected = false;
                }
                storeSportSelection();

                let cat = self.categories.find(x => x.id === item.id);
                if (cat && !cat.isExpandedInLeftMenu) {
                    cat.toggleIsExpandedInLeftMenu();
                }

                return;
            }
            storeSportSelection();

            checkSportSelection();
        }

        const selectSportFilterForCanFilterVideoAndMTWithSport = (item: Instance<typeof FilterItemStore>, value: boolean): void => {
            item.isSelected = value;
            if (value) {
                storeSportSelection();

                let cat = self.categories.find(x => x.id === item.id);
                if (cat && !cat.isExpandedInLeftMenu) {
                    cat.toggleIsExpandedInLeftMenu();
                }
                return;
            }

            storeSportSelection();
            checkSportSelection();
        }

        const checkSportSelection = (): void => {
            let iterable = self.sportFilter.values();
            let item = iterable.next();
            let found = false;
            while (!item.done) {
                if (item.value.id != SportFilterAll && item.value.isSelected) {
                    found = true;
                    break;
                }
                item = iterable.next();
            }
            if (!found) {
                self.sportFilter.get(SportFilterAllAsString).isSelected = true;
                //in fact not necessary to store  storeSportSelection();
            }
        }

        const loadSportSelection = (): Set<number> => {
            let json = env.localStorage.getItem(LOCAL_STORAGE_KEY);
            if (json) {
                let parsed = <number[]>JSON.parse(json);
                if (parsed && parsed.length > 0) {
                    let result = new Set<number>();
                    parsed.map(v => result.add(v));
                    return result;
                }
            }
            return null;
        }
        const storeSportSelection = (): void => {
            let dataToStore: number[] = [];
            self.sportFilter.forEach(item => {
                if (item.isSelected) {
                    dataToStore.push(item.id);
                }
            });
            env.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToStore));
        }

        const storeCollapsedCategory = (id: number, isExpanded: boolean): void => {

            if (!env.settings.canLiveEventsListCollapseSport) {
                return;
            }

            if (!isExpanded) {
                catIdsCollapsedByUser.add(id);
            } else {
                catIdsCollapsedByUser.delete(id);
            }

            env.localStorage.setItem(LOCAL_STORAGE_KEY_COLLAPSED_CATEGORIES, JSON.stringify(Array.from(catIdsCollapsedByUser)));
        }

        const loadCollapsedCategories = (): void => {
            if (!env.settings.canLiveEventsListCollapseSport) {
                return;
            }

            try {
                let json = env.localStorage.getItem(LOCAL_STORAGE_KEY_COLLAPSED_CATEGORIES);
                if (json) {
                    let data = <number[]>JSON.parse(json);
                    catIdsCollapsedByUser = new Set<number>(data);
                }
            } catch (e) {
                env.loggingService.error(e);
                // catIdsCollapsedByUser is already initialized at begin
            }
        }

        const updateIsExpandedInCategory = (cat: ILiveCategoryStore): void => {
            let strCatId = cat.id.toString();

            if (self.sportFilter.get(SportFilterAllAsString).isSelected ||
                (self.sportFilter.has(SportFilterMatchTrackersAsString) && self.sportFilter.get(SportFilterMatchTrackersAsString).isSelected) ||
                (self.sportFilter.has(SportFilterVideosAsString) && self.sportFilter.get(SportFilterVideosAsString).isSelected)) {
                if (!env.settings.canLiveEventsListCollapseSport) {
                    cat.isExpandedInLeftMenu = true;
                } else {
                    const shouldClose: boolean = catIdsCollapsedByUser.has(cat.id);
                    cat.isExpandedInLeftMenu = shouldClose ? false : true;
                }
            } else {
                if (cat.isExpandedInLeftMenu || !env.settings.canLiveEventsListCollapseSport) {
                    cat.isExpandedInLeftMenu = self.sportFilter.has(strCatId) && self.sportFilter.get(strCatId).isSelected;
                }
            }
        }

        const updateIsExpandedInCategoriesOnLeftMenu = (): void => {
            // leave expanded self.favoriteCats.forEach(updateIsExpandedInCategory);
            self.categories.forEach(updateIsExpandedInCategory);
        }

        const updateIsExpandedInCategoriesOnList = (): void => {
            if (env.settings.canLiveEventsListCollapseSport) {
                return;
            }
            self.categories.forEach(updateIsExpandedInCategory);
        }

        const setCategoryIdForOpenedEventsPopup = (categoryId: number, eventsPopupOffset: number = 0): void => {
            if (self.categoryIdForOpenedEventsPopup !== categoryId) {
                self.eventsPopupOffset = eventsPopupOffset;
                self.categoryIdForOpenedEventsPopup = categoryId;
            }
            else {
                self.categoryIdForOpenedEventsPopup = 0;
            }
        }

        const isEventFavorite = (eventId: number, categoryId: number): boolean => {
            return self.favoriteCats.some((cat) => cat.id === categoryId && cat.events.some(e => e.id === eventId));
        }

        return {
            initAsync,
            reloadDataAsync,
            selectSportAsync,
            showDetail,
            hideDetail,
            toggleDetail,
            checkAndOpenDetail,
            addFavoriteEvent,
            removeFavoriteEvent,
            setSportFilterIsSelected,
            toggleSportFilterBySportCategoryId,
            updateIsExpandedInCategoriesOnLeftMenu,
            updateIsExpandedInCategoriesOnList,
            setCategoryIdForOpenedEventsPopup,
            removeAllFavoriteEvents,
            isEventFavorite,
            storeCollapsedCategory
        };
    });

export type ILiveEventListStore = Instance<typeof LiveEventListStore>;